import { useState, useEffect } from 'react';
import { Table, Card, Tag, Space, Input, DatePicker, Button, message, Select } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { apiClient } from '../../lib/api-client';
import { t } from '../../i18n';

const { RangePicker } = DatePicker;

type ClickState = 'CREATED' | 'PREPARED' | 'CONFIRMED' | 'FAILED' | 'CANCELLED';

interface ClickTransaction {
  id: number;
  amount: number;
  state: ClickState;
  clickTransId: string | null;
  clickPaydocId: string | null;
  paidAmount: number | null;
  createdAt: string;
  confirmedAt: string | null;
  cancelledAt: string | null;
  driver: {
    id: number;
    fullName: string;
    phone: string;
  } | null;
}

const formatCurrency = (value: number | null | undefined) =>
  value == null ? '-' : new Intl.NumberFormat('uz-UZ').format(value) + ' ' + t('common.sum');

// CONFIRMED → paid, PREPARED → in-flight, CREATED → started-not-paid, FAILED/CANCELLED → dead.
const STATE_COLOR: Record<ClickState, string> = {
  CREATED: 'default',
  PREPARED: 'blue',
  CONFIRMED: 'green',
  FAILED: 'red',
  CANCELLED: 'volcano',
};

const STATE_LABEL: Record<ClickState, string> = {
  CREATED: 'payments.stateCreated',
  PREPARED: 'payments.statePrepared',
  CONFIRMED: 'payments.stateConfirmed',
  FAILED: 'payments.stateFailed',
  CANCELLED: 'payments.stateCancelled',
};

export const ClickList = () => {
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [state, setState] = useState<'all' | ClickState>('all');

  const [rows, setRows] = useState<ClickTransaction[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const fetchRows = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('/admin/payments/click', {
        params: {
          search: search || undefined,
          state: state === 'all' ? undefined : state,
          startDate: dateRange?.[0]?.format('YYYY-MM-DD'),
          endDate: dateRange?.[1]?.format('YYYY-MM-DD'),
          page,
          limit: pageSize,
        },
      });
      setRows(response.data?.data || []);
      setTotal(response.data?.meta?.total || 0);
    } catch (error) {
      message.error(t('payments.loadError'));
      setRows([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, state]);

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    {
      title: t('table.driver'),
      key: 'driver',
      render: (_: unknown, record: ClickTransaction) =>
        record.driver ? (
          <div>
            <div style={{ fontWeight: 500 }}>{record.driver.fullName || t('common.noName')}</div>
            <div style={{ fontSize: 12, color: '#7F8C8D' }}>{record.driver.phone}</div>
          </div>
        ) : (
          <span style={{ color: '#7F8C8D' }}>-</span>
        ),
    },
    {
      title: t('common.amount'),
      dataIndex: 'amount',
      key: 'amount',
      render: (val: number) => <strong>{formatCurrency(val)}</strong>,
    },
    {
      title: t('payments.state'),
      dataIndex: 'state',
      key: 'state',
      render: (s: ClickState) => <Tag color={STATE_COLOR[s]}>{t(STATE_LABEL[s])}</Tag>,
    },
    {
      title: t('payments.clickTransId'),
      dataIndex: 'clickTransId',
      key: 'clickTransId',
      render: (id: string | null) =>
        id ? <code>#{id}</code> : <span style={{ color: '#7F8C8D' }}>-</span>,
    },
    {
      title: t('payments.paidAmount'),
      dataIndex: 'paidAmount',
      key: 'paidAmount',
      render: (val: number | null) => (
        <span style={{ fontSize: 12, color: '#555' }}>{formatCurrency(val)}</span>
      ),
    },
    {
      title: t('payments.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: t('payments.confirmedAt'),
      dataIndex: 'confirmedAt',
      key: 'confirmedAt',
      render: (date: string | null) =>
        date ? dayjs(date).format('DD.MM.YYYY HH:mm') : <span style={{ color: '#7F8C8D' }}>-</span>,
    },
  ];

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            value={state}
            onChange={(v) => {
              setState(v);
              setPage(1);
            }}
            style={{ width: 180 }}
            options={[
              { value: 'all', label: t('payments.stateAll') },
              { value: 'CREATED', label: t('payments.stateCreated') },
              { value: 'PREPARED', label: t('payments.statePrepared') },
              { value: 'CONFIRMED', label: t('payments.stateConfirmed') },
              { value: 'FAILED', label: t('payments.stateFailed') },
              { value: 'CANCELLED', label: t('payments.stateCancelled') },
            ]}
          />
          <Input
            placeholder={t('payments.clickSearchPlaceholder')}
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 280 }}
            allowClear
            onPressEnter={fetchRows}
          />
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [Dayjs, Dayjs] | null)}
            format="YYYY-MM-DD"
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={fetchRows}>
            {t('common.search')}
          </Button>
        </Space>
      </Card>

      <Card>
        <Table
          dataSource={rows}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
            showSizeChanger: true,
            showTotal: (tot) => `${t('common.total')}: ${tot}`,
          }}
        />
      </Card>
    </div>
  );
};
